import { DOCUMENT, Injectable, computed, effect, inject, signal } from '@angular/core';
import { PRIMARY_COLOR } from '../../config/theme.config';
import { FONT_LINK_ID, fontFamilyStack, resolveFontUrl } from '../../helpers/theme-fonts';
import { LanguageOption, ThemeConfig, ThemeOverrides } from '../../models/theme-settings.model';
import { AppStateService } from './app-state.service';

const STORAGE_KEY = 'master-admin-theme-settings';

/** The app's own values, shown in the panel until something is changed. Nothing here is applied to the page. */
export const DEFAULT_THEME: ThemeConfig = {
  themeMode: 'light',
  menuStyle: 'light',
  headerStyle: 'light',
  pageStyle: 'regular',
  primary: PRIMARY_COLOR,
  secondary: '#6366f1',
  textPrimary: '#1f2937',
  textLabel: '#6b7280',
  textBody: '#374151',
  btnPrimaryBg: PRIMARY_COLOR,
  btnPrimaryText: '#ffffff',
  btnPrimaryBorder: PRIMARY_COLOR,
  btnSecondaryBg: '#6366f1',
  btnSecondaryText: '#ffffff',
  btnSecondaryBorder: '#6366f1',
  btnRadius: '6',
  formText: '#1f2937',
  formBorder: '#d0c4eb',
  formBg: '#ffffff',
  formRadius: '8',
  bodyBg: '#f1f1f6',
  headerBg: '#ffffff',
  headerText: '#374151',
  footerBg: '#1f2937',
  footerText: '#f9fafb',
  footerBgUrl: '',
  logo: './assets/images/brand-logos/erp-logo-icon.png',
  favicon: './assets/images/brand-logos/favicon.ico',
  fontBody: 'Lato',
  fontRtl: 'Cairo',
  fontBase: '100',
};

export const LANGUAGES: readonly LanguageOption[] = [
  { code: 'en', name: 'English', dir: 'ltr', flag: './assets/images/flags/us_flag.svg' },
  { code: 'ar', name: 'Arabic', dir: 'rtl', flag: './assets/images/flags/uae_flag.svg' },
];

type VarUnit = 'px' | 'percent' | 'url';

/** A setting and the CSS custom properties (on <html>) it drives. */
interface VarDef {
  field: keyof ThemeOverrides;
  vars: readonly string[];
  unit?: VarUnit;
  /** Not applied in dark mode (the dark palette owns those colours). */
  lightOnly?: boolean;
}

const VAR_DEFS: readonly VarDef[] = [
  { field: 'secondary', vars: ['--color-secondary', '--saas-secondary'] },
  { field: 'textPrimary', vars: ['--text-color', '--color-defaulttextcolor', '--saas-text-primary'], lightOnly: true },
  { field: 'textLabel', vars: ['--text-color-secondary', '--text-secondary-color', '--color-textmuted', '--saas-text-label'], lightOnly: true },
  { field: 'textBody', vars: ['--saas-text-body'], lightOnly: true },
  { field: 'btnPrimaryBg', vars: ['--saas-btn-primary-bg', '--p-button-primary-background', '--p-button-primary-hover-background'] },
  { field: 'btnPrimaryText', vars: ['--saas-btn-primary-text', '--p-button-primary-color', '--p-button-primary-hover-color'] },
  { field: 'btnPrimaryBorder', vars: ['--saas-btn-primary-border', '--p-button-primary-border-color', '--p-button-primary-hover-border-color'] },
  { field: 'btnSecondaryBg', vars: ['--saas-btn-secondary-bg', '--p-button-secondary-background', '--p-button-secondary-hover-background'] },
  { field: 'btnSecondaryText', vars: ['--saas-btn-secondary-text', '--p-button-secondary-color', '--p-button-secondary-hover-color'] },
  { field: 'btnSecondaryBorder', vars: ['--saas-btn-secondary-border', '--p-button-secondary-border-color', '--p-button-secondary-hover-border-color'] },
  { field: 'btnRadius', vars: ['--saas-btn-radius', '--p-button-border-radius'], unit: 'px' },
  { field: 'formText', vars: ['--saas-form-text'] },
  { field: 'formBorder', vars: ['--saas-form-border', '--input-border-color'] },
  { field: 'formBg', vars: ['--saas-form-bg'] },
  { field: 'formRadius', vars: ['--saas-form-radius'], unit: 'px' },
  { field: 'bodyBg', vars: ['--saas-body-bg', '--surface-ground'], lightOnly: true },
  { field: 'headerBg', vars: ['--saas-header-bg'] },
  { field: 'headerText', vars: ['--saas-header-text'] },
  { field: 'footerBg', vars: ['--saas-footer-bg'] },
  { field: 'footerText', vars: ['--saas-footer-text'] },
  { field: 'footerBgUrl', vars: ['--saas-footer-bg-image'], unit: 'url' },
  { field: 'fontBase', vars: ['--saas-font-base'], unit: 'percent' },
];

/**
 * An attribute on <html> that switches one block of styles/theme/_theme-settings.scss on. It is set only
 * while one of its settings has been changed, so until then the app looks exactly as it did.
 */
const GATES: readonly { attr: string; fields: readonly (keyof ThemeOverrides)[] }[] = [
  { attr: 'data-theme-btn-primary', fields: ['btnPrimaryBg', 'btnPrimaryText', 'btnPrimaryBorder'] },
  { attr: 'data-theme-btn-secondary', fields: ['btnSecondaryBg', 'btnSecondaryText', 'btnSecondaryBorder'] },
  { attr: 'data-theme-btn-radius', fields: ['btnRadius'] },
  { attr: 'data-theme-form-bg', fields: ['formBg'] },
  { attr: 'data-theme-form-text', fields: ['formText'] },
  { attr: 'data-theme-form-border', fields: ['formBorder'] },
  { attr: 'data-theme-form-radius', fields: ['formRadius'] },
  { attr: 'data-theme-header-bg', fields: ['headerBg'] },
  { attr: 'data-theme-header-text', fields: ['headerText'] },
  { attr: 'data-theme-footer-bg', fields: ['footerBg'] },
  { attr: 'data-theme-footer-text', fields: ['footerText'] },
  { attr: 'data-theme-footer-img', fields: ['footerBgUrl'] },
  { attr: 'data-theme-fontbase', fields: ['fontBase'] },
];

function withUnit(value: string, unit?: VarUnit): string {
  const v = value.trim();
  switch (unit) {
    case 'px':
      return /px$/i.test(v) ? v : `${v}px`;
    case 'percent':
      return /%$/.test(v) ? v : `${v}%`;
    case 'url':
      return v.startsWith('url(') ? v : `url(${v})`;
    default:
      return v;
  }
}

/**
 * The Settings panel's state. Light / dark, the primary colour and the text direction stay in
 * AppStateService (the app already remembers and applies those); everything else the panel changes is kept
 * here as "overrides": only what the user changed is written to the page (as CSS custom properties and
 * attributes on <html>), and it is remembered in localStorage.
 */
@Injectable({ providedIn: 'root' })
export class ThemeSettingsService {
  private readonly document = inject(DOCUMENT);
  private readonly appState = inject(AppStateService);

  readonly languages = LANGUAGES;
  readonly overrides = signal<ThemeOverrides>({});
  readonly language = signal<'en' | 'ar'>('en');

  /** Every value the panel shows: the app's own, then the user's changes. */
  readonly theme = computed<ThemeConfig>(() => {
    const state = this.appState.state();
    return {
      ...DEFAULT_THEME,
      ...this.overrides(),
      themeMode: state.theme === 'dark' ? 'dark' : 'light',
      primary: (state.themePrimary || DEFAULT_THEME.primary).toLowerCase(),
    };
  });

  readonly direction = computed<'ltr' | 'rtl'>(() => (this.language() === 'ar' ? 'rtl' : 'ltr'));

  constructor() {
    this.restore();
    effect(() => this.apply(this.overrides(), this.appState.state().theme === 'dark', this.language()));
    effect(() => this.persist(this.overrides(), this.language()));
  }

  // ---- changes ---------------------------------------------------------------------------------------

  patch(partial: ThemeOverrides): void {
    this.overrides.update((current) => ({ ...current, ...partial }));
  }

  setPrimary(hex: string): void {
    const color = hex.trim().toLowerCase();
    if (color) {
      this.appState.updateState({ themePrimary: color });
    }
  }

  setSecondary(hex: string): void {
    const color = hex.trim().toLowerCase();
    if (color) {
      this.patch({ secondary: color, btnSecondaryBg: color, btnSecondaryBorder: color });
    }
  }

  setLanguage(code: 'en' | 'ar'): void {
    this.language.set(code);
    const dir = code === 'ar' ? 'rtl' : 'ltr';
    this.document.documentElement.setAttribute('lang', code);
    this.appState.updateState({ direction: dir });
  }

  /** Back to the app's own look: light, English, the product colour and nothing changed. */
  reset(): void {
    this.overrides.set({});
    this.language.set('en');
    this.document.documentElement.setAttribute('lang', 'en');
    this.appState.applyReset();
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* storage unavailable */
    }
  }

  // ---- applying to the page --------------------------------------------------------------------------

  private apply(o: ThemeOverrides, dark: boolean, language: 'en' | 'ar'): void {
    const html = this.document.documentElement;

    for (const def of VAR_DEFS) {
      const raw = o[def.field];
      const on = typeof raw === 'string' && raw.trim() !== '' && !(def.lightOnly && dark);
      for (const name of def.vars) {
        if (on) {
          html.style.setProperty(name, withUnit(raw as string, def.unit));
        } else {
          html.style.removeProperty(name);
        }
      }
    }

    for (const gate of GATES) {
      const active = gate.fields.some((field) => {
        const v = o[field];
        return typeof v === 'string' && v.trim() !== '';
      });
      if (active) {
        html.setAttribute(gate.attr, '');
      } else {
        html.removeAttribute(gate.attr);
      }
    }

    html.setAttribute('data-menu-variant', o.menuStyle ?? 'light');
    html.setAttribute('data-header-variant', o.headerStyle ?? 'light');
    html.setAttribute('data-page-style', o.pageStyle ?? 'regular');

    this.applyFont(o, language);
    this.applyFavicon(o.favicon);
  }

  private applyFont(o: ThemeOverrides, language: 'en' | 'ar'): void {
    const html = this.document.documentElement;
    const name = (language === 'ar' ? o.fontRtl : undefined) || o.fontBody;
    if (!name) {
      html.removeAttribute('data-theme-font');
      html.style.removeProperty('--saas-font-family');
      return;
    }
    let link = this.document.getElementById(FONT_LINK_ID) as HTMLLinkElement | null;
    if (!link) {
      link = this.document.createElement('link');
      link.id = FONT_LINK_ID;
      link.rel = 'stylesheet';
      this.document.head.appendChild(link);
    }
    const href = resolveFontUrl(name);
    if (link.getAttribute('href') !== href) {
      link.setAttribute('href', href);
    }
    html.style.setProperty('--saas-font-family', fontFamilyStack(name));
    html.setAttribute('data-theme-font', '');
  }

  /** The tab icon the page came with, kept the first time it is replaced so Reset can bring it back. */
  private originalFavicon: { href: string; type: string } | null = null;

  private applyFavicon(href: string | undefined): void {
    const existing = this.document.getElementById('app-favicon') as HTMLLinkElement | null;
    if (!href) {
      // Nothing chosen: put the page's own icon back if one was replaced.
      if (existing && this.originalFavicon) {
        this.setFaviconLink(this.originalFavicon.href, this.originalFavicon.type);
      }
      return;
    }
    if (existing?.getAttribute('data-src') === href) {
      return;
    }
    if (!this.originalFavicon) {
      const current = this.document.querySelector("link[rel*='icon']") as HTMLLinkElement | null;
      this.originalFavicon = { href: current?.getAttribute('href') ?? './assets/images/brand-logos/erp-logo-icon.png', type: current?.type || 'image/png' };
    }
    this.setFaviconLink(href, href.endsWith('.svg') ? 'image/svg+xml' : 'image/x-icon');
  }

  private setFaviconLink(href: string, type: string): void {
    this.document.querySelectorAll("link[rel*='icon']").forEach((el) => el.remove());
    const link = this.document.createElement('link');
    link.id = 'app-favicon';
    link.rel = 'icon';
    link.type = type;
    link.href = href;
    link.setAttribute('data-src', href);
    this.document.head.appendChild(link);
  }

  // ---- remembered between visits ---------------------------------------------------------------------

  private restore(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return;
      }
      const saved = JSON.parse(raw) as { overrides?: ThemeOverrides; language?: 'en' | 'ar' };
      if (saved.overrides && typeof saved.overrides === 'object') {
        this.overrides.set(saved.overrides);
      }
      if (saved.language === 'ar' || saved.language === 'en') {
        this.language.set(saved.language);
        this.document.documentElement.setAttribute('lang', saved.language);
      }
    } catch {
      /* unreadable: start from the app's own look */
    }
  }

  private persist(overrides: ThemeOverrides, language: 'en' | 'ar'): void {
    try {
      if (Object.keys(overrides).length === 0 && language === 'en') {
        localStorage.removeItem(STORAGE_KEY);
      } else {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ overrides, language }));
      }
    } catch {
      /* storage unavailable */
    }
  }
}
